import { blogApi, sitesApi, ApiError } from '../../api/client';
import type { BlogPost, BlogPostStatus } from '../../schemas/validation';
import type { BlogManagerState } from './useBlogManagerState';
import type { BlogCustomColors } from './types';

interface HandlerDependencies {
  siteId: string | undefined;
  navigate: (path: string) => void;
  state: BlogManagerState;
  /** Keep unpublished-summary / badges in sync after post delete or publish toggle */
  onAfterBlogPostMutation?: () => void | Promise<void>;
}

export const createBlogManagerHandlers = ({
  siteId,
  navigate,
  state,
  onAfterBlogPostMutation,
}: HandlerDependencies) => {
  const postsPerPage = 10;

  const refreshStatsFromServer = async () => {
    if (!siteId) return;
    try {
      const blogData = await blogApi.get(siteId);
      state.setStats(blogData.stats);
    } catch (e) {
      console.error('Failed to refresh blog stats', e);
    }
  };

  const notifyPostListMutationComplete = async () => {
    try {
      await onAfterBlogPostMutation?.();
    } catch (e) {
      console.error('Failed to refetch unpublished summary', e);
    }
  };

  const loadInitialData = async () => {
    try {
      state.setIsLoading(true);

      // Load site info
      const allSites = await sitesApi.list();
      const currentSite = allSites.find(s => s.id === siteId);

      if (!currentSite) {
        navigate('/dashboard');
        return;
      }

      state.setSite(currentSite);

      // Load blog data
      try {
        const blogData = await blogApi.get(siteId!);
        state.setBlog(blogData.blog);
        state.setStats(blogData.stats);

        // Load tags
        const tagsData = await blogApi.getTags(siteId!);
        state.setTags(tagsData.tags);
      } catch (error: any) {
        if (error?.status === 404) {
          // Blog not installed, redirect back to site manager
          navigate(`/sites/${siteId}`);
          return;
        }
        throw error;
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        navigate('/login');
      } else {
        console.error('Failed to load blog data:', error);
        navigate(`/sites/${siteId}`);
      }
    } finally {
      state.setIsLoading(false);
    }
  };

  const loadPosts = async () => {
    if (!siteId) return;

    try {
      state.setIsLoadingPosts(true);

      const params: {
        page: number;
        limit: number;
        status?: BlogPostStatus;
        tag?: string;
      } = {
        page: state.currentPage,
        limit: postsPerPage,
      };

      if (state.statusFilter !== 'all') {
        params.status = state.statusFilter;
      }

      if (state.selectedTag) {
        params.tag = state.selectedTag;
      }

      const response = await blogApi.posts.list(siteId, params);
      state.setPosts(response.posts);
      state.setTotalPages(response.pagination.totalPages);
      state.setTotalPosts(response.pagination.total);
    } catch (error) {
      console.error('Failed to load posts:', error);
      state.showNotification('Failed to load posts', 'error');
    } finally {
      state.setIsLoadingPosts(false);
    }
  };

  const handleDeletePost = async () => {
    if (!siteId || !state.postToDelete) return;

    try {
      state.setIsDeletingPost(true);
      await blogApi.posts.delete(siteId, state.postToDelete.id);
      state.setPosts(prev => prev.filter(p => p.id !== state.postToDelete!.id));
      state.setShowDeleteModal(false);
      state.setPostToDelete(null);
      state.showNotification('Post deleted successfully', 'success');

      await refreshStatsFromServer();
      await notifyPostListMutationComplete();
    } catch (error) {
      console.error('Failed to delete post:', error);
      state.showNotification('Failed to delete post', 'error');
    } finally {
      state.setIsDeletingPost(false);
    }
  };

  const handleTogglePostPublish = async (post: BlogPost) => {
    if (!siteId) return;

    // Match BlogPostsList: live = published status OR publishedAt (public/list truth)
    const isPublished = post.status === 'published' || !!post.publishedAt;
    const newStatus: BlogPostStatus = isPublished ? 'draft' : 'published';

    try {
      state.setIsTogglingPublish(post.id);
      const response = await blogApi.posts.setPublished(siteId, post.id, newStatus);
      state.setPosts(prev => prev.map(p => (p.id === post.id ? response.post : p)));
      state.showNotification(
        newStatus === 'published' ? 'Post published!' : 'Post unpublished',
        'success'
      );

      await refreshStatsFromServer();
      await notifyPostListMutationComplete();
    } catch (error) {
      console.error('Failed to toggle post publish status:', error);
      state.showNotification('Failed to update post status', 'error');
    } finally {
      state.setIsTogglingPublish(null);
    }
  };

  const handleToggleBlogPublish = async () => {
    if (!siteId || !state.blog) return;

    try {
      state.setIsTogglingBlogPublish(true);
      const response = await blogApi.setPublished(siteId, !state.blog.isPublished);
      state.setBlog(response.blog);
      state.showNotification(
        response.blog.isPublished
          ? 'Blog is now visible to visitors!'
          : 'Blog hidden from visitors',
        'success'
      );
    } catch (error) {
      console.error('Failed to toggle blog publish status:', error);
      state.showNotification('Failed to update blog visibility', 'error');
    } finally {
      state.setIsTogglingBlogPublish(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!siteId) return;

    try {
      state.setIsSavingSettings(true);
      // Include custom colors in the update - filter out empty values
      const filteredColors = Object.fromEntries(
        Object.entries(state.customColors).filter(([_, v]) => v && v.trim() !== '')
      );
      const hasCustomColors = Object.keys(filteredColors).length > 0;

      const response = await blogApi.update(siteId, {
        ...state.settingsForm,
        customColors: hasCustomColors ? filteredColors : null,
      });
      state.setBlog(response.blog);
      state.setShowSettingsPanel(false);
      state.setShowColorPanel(false);
      state.showNotification('Blog settings saved successfully', 'success');
    } catch (error) {
      console.error('Failed to save settings:', error);
      state.showNotification('Failed to save settings', 'error');
    } finally {
      state.setIsSavingSettings(false);
    }
  };

  // Handle custom color changes
  const handleColorChange = (key: keyof BlogCustomColors, value: string) => {
    state.setCustomColors(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // Clear a specific color
  const clearColor = (key: keyof BlogCustomColors) => {
    state.setCustomColors(prev => {
      const newColors = { ...prev };
      delete newColors[key];
      return newColors;
    });
  };

  // Reset all custom colors
  const resetAllColors = () => {
    state.setCustomColors({});
  };

  return {
    loadInitialData,
    loadPosts,
    handleDeletePost,
    handleTogglePostPublish,
    handleToggleBlogPublish,
    handleSaveSettings,
    handleColorChange,
    clearColor,
    resetAllColors,
    postsPerPage,
  };
};
